import MapFeature from './MapFeature'
import Marker from '../markers/Marker'
import { gettext } from '@bikemap/js/services'
import { renderImg } from '@bikemap/js/utility'
import MapContent from './MapContent'

// Default/fallback values
const ID = 'route'
const LINE_ID = 'route-line'
const OUTLINE_ID = 'route-outline'
const lineBaseLayer = {
    type: 'line',
    layout: {
        'line-join': 'round',
        'line-cap': 'round',
    },
}

/**
 * Shows a route polyline with markers on the map.
 */
export default class Route extends MapFeature {

    /** @type {Marker|null} */
    _startMarker

    /** @type {Marker|null} */
    _destMarker

    /** @type {MapContent} */
    _mapContentFeature

    /**
     * @param {Object|null} [geometry] as GeoJSON
     * @param {Object} [options]
     * @param {string} [options.id] Unique ID for MapContent, line, and outline (useful when adding multiple routes)
     * @param {boolean} [options.isInactive=false] If route should be shown as inactive/gray
     * @param {Marker} [options.startMarker] Override start marker
     * @param {Marker} [options.destMarker] Override destination marker
     */
    constructor(geometry = null, options = {}) {
        super()

        const coordinates = this._getGeometryCoordinates(geometry)
        const { start, end } = this._formatMarkerPositions(coordinates)

        this._startMarker = (options.startMarker === undefined) ? new Marker(
            start || [0, 0],
            renderImg('markers/start.svg', gettext('Start'), '27', '39'),
            [0, -16]
        ) : options.startMarker

        this._destMarker = (options.destMarker === undefined) ? new Marker(
            end || [0, 0],
            renderImg('markers/destination.svg', gettext('Destination'), '31', '44'),
            [0, -20]
        ) : options.destMarker

        const source = {
            type: 'geojson',
            data: this._formatSourceData(geometry),
        }

        const isInactive = options.isInactive === true

        const lineId = options.id ? options.id + '-line' : null
        const lineLayer = this._formatLineLayer(lineId, isInactive)

        const outlineId = options.id ? options.id + '-outline' : null
        const outlineLayer = this._formatOutlineLayer(outlineId, isInactive)

        this._mapContentFeature = new MapContent(options.id || ID, source, outlineLayer, lineLayer)
    }

    _onAdd(map) {
        this._destMarker && this._destMarker.addTo(map)
        this._startMarker && this._startMarker.addTo(map)

        this._mapContentFeature.addTo(map)
    }

    _onRemove() {
        this._mapContentFeature.remove()

        this._destMarker && this._destMarker.remove()
        this._startMarker && this._startMarker.remove()
    }

    /**
     * Get layer object for route line.
     * @param {string} [id]
     * @param {boolean} [isInactive] Determines colors
     * @returns {Object}
     */
    _formatLineLayer(id, isInactive) {
        return {
            ...lineBaseLayer,
            id: id || LINE_ID,
            paint: {
                'line-width': [
                    'interpolate', ['linear'], ['zoom'],
                    // zoom is <=10 -> 3px
                    10, 3,
                    // zoom is >=22 -> 6px
                    22, 6,
                ],
                'line-color': isInactive ? '#cbd5dc' : '#1381fa',
            },
        }
    }

    /**
     * Get layer object for route line.
     * @param {string} [id]
     * @param {boolean} [isInactive] Determines colors
     * @returns {Object}
     */
    _formatOutlineLayer(id, isInactive) {
        return {
            ...lineBaseLayer,
            id: id || OUTLINE_ID,
            paint: {
                'line-width': [
                    'interpolate', ['linear'], ['zoom'],
                    // zoom is <=10 -> 6px
                    10, 6,
                    // zoom is >=22 -> 12px
                    22, 12,
                ],
                'line-color': isInactive ? '#879ead' : '#0069db',
            },
        }
    }

    /**
     * Get GeoJSON data in standard format.
     * @param {Object|null} [geometry]
     * @returns {Object} GeoJSON data
     */
    _formatSourceData(geometry = {}) {
        return {
            type: 'Feature',
            properties: {},
            geometry,
        }
    }

    /**
     * Get start (first) and end (last) pair of LngLat values from provided coordinates.
     * @param {array} coordinates
     * @returns {{start: (Object|null), end: (Object|null)}} LngLat pair
     */
    _formatMarkerPositions(coordinates) {
        return {
            start: coordinates[0] || null,
            end: coordinates[coordinates.length - 1] || null,
        }
    }

    /**
     * Try to find coordinates from provided geometry.
     * @param {Object} geometry
     * @returns {array}
     */
    _getGeometryCoordinates(geometry) {
        try {
            if (geometry.type === 'MultiLineString') {
                return geometry.coordinates[0]
            }
            return geometry.coordinates
        } catch (e) {
            return []
        }
    }

    /**
     * Update `source.data.geometry` of current MapContent feature.
     * @param {Object|null} [geometry] If set to null, route and markers will be hidden.
     * @returns {void}
     */
    setGeometryData(geometry) {
        // Get GeoJSON data with new geometry points
        const sourceData = this._formatSourceData(geometry)

        // Update geometry/points of current route
        this._mapContentFeature.setData(sourceData)

        // Also update start/destination markers from provided first/last coordinates
        const coordinates = this._getGeometryCoordinates(geometry)
        const { start, end } = this._formatMarkerPositions(coordinates)

        if (this._startMarker) {
            if (start) {
                this._startMarker.setLngLat(start)
                this._startMarker.setOpacity('full')
            } else {
                // Keep marker but make it invisible
                this._startMarker.setOpacity('transparent')
            }
        }
        if (this._destMarker) {
            if (end) {
                this._destMarker.setLngLat(end)
                this._destMarker.setOpacity('full')
            } else {
                // Keep marker but make it invisible
                this._destMarker.setOpacity('transparent')
            }
        }
    }

}
